import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bs_token'));
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState(localStorage.getItem('bs_theme') || 'dark');

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await authAPI.me();
      setUser(res.data);
      const userTheme = res.data.theme_preference || 'dark';
      setThemeState(userTheme);
      localStorage.setItem('bs_theme', userTheme);
    } catch {
      localStorage.removeItem('bs_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.className = theme;
  }, [theme]);

  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('bs_theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    document.documentElement.className = newTheme;
    if (user) {
      try { await authAPI.updatePreferences({ theme: newTheme }); } catch { /* ignore */ }
    }
  };

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('bs_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    const userTheme = res.data.user.theme_preference || 'dark';
    setThemeState(userTheme);
    localStorage.setItem('bs_theme', userTheme);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await authAPI.register({ email, password, name });
    localStorage.setItem('bs_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('bs_token');
    localStorage.removeItem('bs_theme');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try { const res = await authAPI.me(); setUser(res.data); } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, theme, setTheme, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
